/**
 * The recipes a board offers, as cards. One the board cannot satisfy stays in
 * the list, disabled, saying which kind of column it is missing, so the
 * gallery reads the same on every board of a type.
 */
import { useTranslation } from 'react-i18next';
import { BookOpenIcon } from 'lucide-react';

import type { AutomationAction, AutomationTrigger } from '@/entities/tracker-automation';

import type { RecipeId, RecipeNeed, RecipeResult } from '../../lib/automation-recipes';

interface AutomationRecipeGalleryProps {
  recipes: Array<{ id: RecipeId } & RecipeResult>;
  onPick: (
    rule: { trigger: AutomationTrigger; actions: AutomationAction[] },
    name: string,
    id: RecipeId,
  ) => void;
}

export function AutomationRecipeGallery({ recipes, onPick }: AutomationRecipeGalleryProps) {
  const { t } = useTranslation('trackers');
  const title = (id: RecipeId) => t(`trackersBoard.automationRecipes.recipes.${id}.title`);
  const missing = (need: RecipeNeed) => t(`trackersBoard.automationRecipes.needs.${need}`);

  return (
    <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {recipes.map((recipe) => (
        <li key={recipe.id}>
          <button
            type="button"
            disabled={!('rule' in recipe)}
            onClick={() => {
              if ('rule' in recipe) onPick(recipe.rule, title(recipe.id), recipe.id);
            }}
            className="flex h-full w-full flex-col gap-1 rounded-lg border border-border p-3 text-left hover:bg-accent/40 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-foreground">
              <BookOpenIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              {title(recipe.id)}
            </span>
            <span className="text-xs text-muted-foreground">
              {t(`trackersBoard.automationRecipes.recipes.${recipe.id}.description`)}
            </span>
            {'missing' in recipe ? (
              <span className="text-xs text-warning-foreground">{missing(recipe.missing)}</span>
            ) : null}
          </button>
        </li>
      ))}
    </ul>
  );
}
